import { FONTS, PRO_FONTS, COLOR_SWATCHES } from "../../lib/constants";
import { useState } from "react";
import {
  WEIGHTS, KERNING_OPTIONS, ANTIALIAS_OPTIONS, autoLeading, baseAlign,
} from "../../lib/typography";
import { Slider } from "../ui/slider";
import { useFonts } from "../../context/FontsContext";
import { useSubscription } from "../../context/SubscriptionContext";
import {
  TextAlignLeft, TextAlignCenter, TextAlignRight, TextAlignJustify, TextB, TextItalic, ArrowsOutLineVertical, Crown,
} from "@phosphor-icons/react";

const Header = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-semibold mb-3">{children}</p>
);

// Scrubby input: drag the label left/right to change, or type a value.
export function ScrubInput({ value, onChange, onStart, step = 1, min, max, label, suffix, testid }) {
  const startScrub = (e) => {
    e.preventDefault();
    onStart && onStart();
    const sx = e.clientX;
    const sv = Number(value) || 0;
    const move = (ev) => {
      let nv = sv + (ev.clientX - sx) * step;
      nv = step < 1 ? Math.round(nv * 100) / 100 : Math.round(nv);
      if (min != null) nv = Math.max(min, nv);
      if (max != null) nv = Math.min(max, nv);
      onChange(nv);
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  return (
    <div className="flex items-center rounded-lg bg-black/40 border border-white/10 focus-within:border-blue-500/60 h-9 overflow-hidden" title="Drag label to change">
      <span onMouseDown={startScrub} className="px-2 h-full flex items-center text-[10px] font-semibold text-zinc-400 select-none border-r border-white/10 hover:text-blue-400" style={{ cursor: "ew-resize" }}>
        {label}
      </span>
      <input
        type="number"
        value={value}
        onFocus={onStart}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        data-testid={testid}
        className="flex-1 w-full min-w-0 bg-transparent px-2 text-sm text-white outline-none"
      />
      {suffix && <span className="pr-2 text-[10px] text-zinc-500 select-none">{suffix}</span>}
    </div>
  );
}

const Select = ({ value, onChange, onStart, options, testid, style }) => (
  <select
    value={value}
    onChange={(e) => { onStart(); onChange(e.target.value); }}
    data-testid={testid}
    style={style}
    className="w-full h-9 rounded-lg bg-[#161616] border border-white/10 px-2 text-sm text-white outline-none focus:border-blue-500/60"
  >
    {options.map((o) => {
      const val = o.v ?? o;
      const label = o.l ?? o;
      const optStyle = {
        backgroundColor: "#161616",
        color: "#ffffff",
        ...(o.style || (typeof o === "string" ? { fontFamily: `'${o}', sans-serif` } : {})),
      };
      return <option key={val} value={val} style={optStyle}>{label}</option>;
    })}
  </select>
);

export function CharacterPanel({ el, onUpdate, onStart, wordSel, onWordStyle, onClearWordStyles }) {
  const u = onUpdate;
  const { families } = useFonts();
  const { isPro, openUpgrade } = useSubscription();
  const fontOptions = [
    ...FONTS.filter((f) => !PRO_FONTS.includes(f)),
    ...families.filter((f) => !FONTS.includes(f) && !PRO_FONTS.includes(f)),
    ...PRO_FONTS.map((f) => ({ v: f, l: `${f}  ·  PRO 👑`, style: { fontFamily: `'${f}', sans-serif` } })),
  ];
  const leadingValue = el.leadingAuto === false ? (el.leading ?? autoLeading(el)) : autoLeading(el);
  const wordActive = !!(wordSel && wordSel.end > wordSel.start);
  const pickFont = (v) => {
    if (PRO_FONTS.includes(v) && !isPro) { openUpgrade(`"${v}" is a Pro font. Upgrade to unlock premium display fonts.`); return; }
    onStart(); u({ fontFamily: v });
  };
  const setColor = (c) => {
    if (wordActive) {
      if (!isPro) { openUpgrade("Per-word coloring is a Pro feature. Upgrade to style individual words."); return; }
      onWordStyle && onWordStyle({ color: c });
    } else { onStart(); u({ color: c }); }
  };
  const toggleDynamic = () => {
    if (!isPro) { openUpgrade("Dynamic text (auto fill-to-width) is a Pro feature. Upgrade for bold, edge-to-edge headlines."); return; }
    onStart(); u({ autoFit: !el.autoFit });
  };
  const applyWordStroke = () => {
    if (!isPro) { openUpgrade("Per-word strokes are a Pro feature. Upgrade to outline individual words."); return; }
    onWordStyle && onWordStyle({ strokeColor: wStroke.color, strokeWidth: wStroke.width });
  };
  const [wStroke, setWStroke] = useState({ color: "#000000", width: 4 });

  return (
    <div data-testid="character-panel">
      <Header>Character</Header>

      <div className="mb-2">
        <Select value={el.fontFamily} onStart={onStart} onChange={pickFont} options={fontOptions} testid="char-font-family" style={{ fontFamily: `'${el.fontFamily}', sans-serif` }} />
      </div>
      <div className="mb-2">
        <Select value={el.fontWeight || "400"} onStart={onStart} onChange={(v) => u({ fontWeight: v })} options={WEIGHTS} testid="char-weight" />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        {el.autoFit ? (
          <ScrubInput label="Size" suffix="%" value={Math.round((el.fitScale ?? 1) * 100)} step={1} min={10} onStart={onStart} onChange={(v) => u({ fitScale: Math.max(0.1, v / 100) })} testid="char-size" />
        ) : (
          <ScrubInput label="Size" value={el.fontSize} step={1} min={1} onStart={onStart} onChange={(v) => u({ fontSize: Math.max(1, v) })} testid="char-size" />
        )}
        <div className="flex items-center gap-1">
          <ScrubInput label="Lead" value={leadingValue} step={1} min={-30} onStart={onStart} onChange={(v) => u({ leading: v, leadingAuto: false })} testid="char-leading" />
          <button
            onClick={() => { onStart(); u({ leadingAuto: !(el.leadingAuto !== false) }); }}
            data-testid="char-leading-auto"
            className={`h-9 px-2 rounded-lg text-[10px] font-semibold shrink-0 transition-colors ${el.leadingAuto !== false ? "bg-blue-600 text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
            title="Auto leading"
          >
            Auto
          </button>
        </div>
      </div>

      <button
        onClick={toggleDynamic}
        data-testid="char-autofit"
        title="Dynamic text — each line auto-scales to fill the full width of the box"
        className={`w-full h-9 mb-1 rounded-lg flex items-center justify-center gap-2 text-xs font-semibold transition-colors ${el.autoFit ? "bg-blue-600 text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
      >
        <ArrowsOutLineVertical size={15} /> Dynamic text {el.autoFit ? "· On" : "· Off"} {!isPro && <Crown weight="fill" size={12} className="text-amber-300" />}
      </button>
      {el.autoFit && (
        <p className="text-[10px] text-zinc-500 mb-3">
          Each line scales to span the full width. <b>Size</b> scales all lines together (drag the label), <b>Lead</b> sets even line spacing (can go negative to −30), <b>VA</b> sets even letter spacing.
        </p>
      )}

      <div className="grid grid-cols-2 gap-2 mb-2">
        <Select value={el.kerning || "metrics"} onStart={onStart} onChange={(v) => u({ kerning: v })} options={KERNING_OPTIONS} testid="char-kerning" />
        <ScrubInput label="VA" value={el.tracking ?? 0} step={5} onStart={onStart} onChange={(v) => u({ tracking: v })} testid="char-tracking" />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <ScrubInput label="V" suffix="%" value={el.verticalScale ?? 100} step={1} min={1} onStart={onStart} onChange={(v) => u({ verticalScale: Math.max(1, v) })} testid="char-vscale" />
        <ScrubInput label="H" suffix="%" value={el.horizontalScale ?? 100} step={1} min={1} onStart={onStart} onChange={(v) => u({ horizontalScale: Math.max(1, v) })} testid="char-hscale" />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <ScrubInput label="Aᵇ" value={el.baselineShift ?? 0} step={1} onStart={onStart} onChange={(v) => u({ baselineShift: v })} testid="char-baseline" />
        <Select value={el.antiAlias || "smooth"} onStart={onStart} onChange={(v) => u({ antiAlias: v })} options={ANTIALIAS_OPTIONS} testid="char-antialias" />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => { onStart(); u({ fauxBold: !el.fauxBold }); }} data-testid="char-faux-bold"
          className={`h-9 flex-1 rounded-lg flex items-center justify-center gap-1 text-xs font-medium transition-colors ${el.fauxBold ? "bg-blue-600 text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`} title="Faux Bold">
          <TextB size={15} /> Bold
        </button>
        <button onClick={() => { onStart(); u({ fauxItalic: !el.fauxItalic }); }} data-testid="char-faux-italic"
          className={`h-9 flex-1 rounded-lg flex items-center justify-center gap-1 text-xs font-medium transition-colors ${el.fauxItalic ? "bg-blue-600 text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`} title="Faux Italic">
          <TextItalic size={15} /> Italic
        </button>
      </div>

      <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">
        Text color{wordActive && <span className="text-blue-400 normal-case tracking-normal"> · applies to selected word</span>}
      </p>
      <div className="flex items-center gap-2 mb-2">
        <label className="relative h-8 w-8 rounded-lg border border-white/15 overflow-hidden cursor-pointer shrink-0" style={{ background: el.color }}>
          <input type="color" value={el.color || "#000000"} onMouseDown={onStart} onChange={(e) => setColor(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="char-color" />
        </label>
        <div className="grid grid-cols-8 gap-1.5 flex-1">
          {COLOR_SWATCHES.slice(0, 16).map((c) => (
            <button key={c} onClick={() => setColor(c)} className="h-4 w-4 rounded border border-white/15 hover:scale-110 transition-transform" style={{ background: c }} data-testid={`char-swatch-${c}`} />
          ))}
        </div>
      </div>

      {wordActive && (
        <div className="rounded-lg border border-blue-500/40 bg-blue-600/10 p-2.5 mb-2" data-testid="word-style-panel">
          <p className="text-[10px] uppercase tracking-[0.18em] text-blue-300 font-semibold mb-2">Selected word · stroke</p>
          <div className="flex items-center gap-2 mb-2">
            <label className="relative h-7 w-7 rounded-md border border-white/15 overflow-hidden cursor-pointer shrink-0" style={{ background: wStroke.color }}>
              <input type="color" value={wStroke.color} onChange={(e) => setWStroke((s) => ({ ...s, color: e.target.value }))} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="word-stroke-color" />
            </label>
            <span className="text-[11px] text-zinc-300 tabular-nums w-9 text-right">{wStroke.width}px</span>
            <button
              onClick={applyWordStroke}
              data-testid="word-stroke-apply"
              className="flex-1 h-7 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-semibold transition-colors flex items-center justify-center gap-1"
            >
              Apply stroke {!isPro && <Crown weight="fill" size={11} className="text-amber-300" />}
            </button>
            <button
              onClick={() => onWordStyle && onWordStyle({ strokeWidth: 0 })}
              data-testid="word-stroke-remove"
              className="h-7 w-7 rounded-md border border-white/10 text-zinc-400 hover:text-white hover:border-red-500/50 hover:bg-red-600/10 transition-all flex items-center justify-center leading-none"
              title="Remove stroke from word"
            >
              ×
            </button>
          </div>
          <Slider value={[wStroke.width]} min={1} max={55} step={1} onValueChange={(v) => setWStroke((s) => ({ ...s, width: v[0] }))} data-testid="word-stroke-width" />
        </div>
      )}

      {Array.isArray(el.runs) && el.runs.length > 0 && (
        <button
          onClick={() => onClearWordStyles && onClearWordStyles()}
          data-testid="char-reset-word-styles"
          className="w-full h-8 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 text-[11px] font-medium transition-colors mb-1"
        >
          Reset all word colors
        </button>
      )}
    </div>
  );
}

const ALIGN_BTNS = [
  { id: "left", icon: TextAlignLeft, title: "Left" },
  { id: "center", icon: TextAlignCenter, title: "Center" },
  { id: "right", icon: TextAlignRight, title: "Right" },
  { id: "justifyLastLeft", icon: TextAlignJustify, tag: "L", title: "Justify, last left" },
  { id: "justifyLastCenter", icon: TextAlignJustify, tag: "C", title: "Justify, last center" },
  { id: "justifyLastRight", icon: TextAlignJustify, tag: "R", title: "Justify, last right" },
  { id: "justifyAll", icon: TextAlignJustify, tag: "A", title: "Justify all" },
];

export function ParagraphPanel({ el, onUpdate, onStart }) {
  const u = onUpdate;
  const current = el.align || el.textAlign || "left";

  return (
    <div data-testid="paragraph-panel">
      <Header>Paragraph</Header>

      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {ALIGN_BTNS.map((a) => {
          const Icon = a.icon;
          const active = current === a.id;
          return (
            <button
              key={a.id}
              onClick={() => { onStart(); u({ align: a.id, textAlign: baseAlign(a.id) }); }}
              data-testid={`para-align-${a.id}`}
              title={a.title}
              className={`relative h-9 w-9 rounded-lg flex items-center justify-center transition-colors ${active ? "bg-blue-600 text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
            >
              <Icon size={16} />
              {a.tag && <span className="absolute bottom-0.5 right-1 text-[8px] font-bold leading-none">{a.tag}</span>}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <ScrubInput label="◧" value={el.indentLeft ?? 0} step={1} min={0} onStart={onStart} onChange={(v) => u({ indentLeft: v })} testid="para-indent-left" />
        <ScrubInput label="◨" value={el.indentRight ?? 0} step={1} min={0} onStart={onStart} onChange={(v) => u({ indentRight: v })} testid="para-indent-right" />
      </div>

      <div className="mb-2">
        <ScrubInput label="First line ⇥" value={el.indentFirst ?? 0} step={1} onStart={onStart} onChange={(v) => u({ indentFirst: v })} testid="para-indent-first" />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-1">
        <ScrubInput label="▲ Before" value={el.spaceBefore ?? 0} step={1} min={0} onStart={onStart} onChange={(v) => u({ spaceBefore: v })} testid="para-space-before" />
        <ScrubInput label="▼ After" value={el.spaceAfter ?? 0} step={1} min={0} onStart={onStart} onChange={(v) => u({ spaceAfter: v })} testid="para-space-after" />
      </div>
      <p className="text-[10px] text-zinc-500 mt-1">Spacing applies between lines/paragraphs (press Enter for new lines).</p>
    </div>
  );
}
